import { useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { Loader2, Search, CheckCircle2, ChevronDown, ChevronRight, Layers } from "lucide-react";

import { SiteHeader } from "@/components/site/SiteHeader";
import { AdminNav } from "@/components/admin/AdminNav";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  reconcileStripePayments,
  type StripeWebhookEventsFilters,
  type ReconciledPayment,
} from "@/lib/stripe-webhook-events.functions";
import { formatStripeAmount } from "@/lib/stripe-event-summary";

export const Route = createFileRoute("/_authenticated/admin/stripe-reconciliation")({
  head: () => ({
    meta: [
      { title: "Admin · Reconciliación de pagos Stripe — Mostrando México" },
      {
        name: "description",
        content:
          "Agrupa los eventos de Stripe por Payment ID y muestra qué evento aportó el monto deduplicado.",
      },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: StripeReconciliationPage,
});

type FormState = {
  from: string;
  to: string;
  env: "" | "sandbox" | "live";
  currency: string;
  search: string;
};

const EMPTY: FormState = { from: "", to: "", env: "", currency: "", search: "" };

function toFilters(f: FormState): Omit<StripeWebhookEventsFilters, "cursor"> {
  const iso = (v: string) => (v ? new Date(v).toISOString() : undefined);
  const txt = (v: string) => v.trim() || undefined;
  return {
    from: iso(f.from),
    to: iso(f.to),
    env: f.env || undefined,
    currency: txt(f.currency),
    search: txt(f.search),
    status: "all",
    category: "all",
    outcome: "all",
    pageSize: 50,
  };
}

function fmtDate(iso: string) {
  return new Date(iso).toLocaleString("es-MX");
}

function keyKindLabel(k: ReconciledPayment["keyKind"]) {
  return k === "payment_intent"
    ? "Payment Intent"
    : k === "charge"
      ? "Charge"
      : k === "checkout_session"
        ? "Checkout Session"
        : "Evento suelto";
}

function PaymentGroup({ p }: { p: ReconciledPayment }) {
  const [open, setOpen] = useState(p.eventCount > 1);
  const counted = p.events.find((e) => e.countedAmount);
  return (
    <div className="rounded-lg border bg-card">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-start gap-3 p-4 text-left"
      >
        {open ? (
          <ChevronDown className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
        ) : (
          <ChevronRight className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
        )}
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-mono text-sm break-all">{p.paymentKey}</span>
            <span className="rounded border px-1.5 py-0.5 text-[10px] uppercase text-muted-foreground">
              {keyKindLabel(p.keyKind)}
            </span>
            <span className="rounded border px-1.5 py-0.5 text-[10px] uppercase text-muted-foreground">
              {p.env}
            </span>
          </div>
          <p className="mt-1 text-xs text-muted-foreground">
            {p.customer_name ?? "Cliente no disponible"}
            {p.customer_email ? ` · ${p.customer_email}` : ""}
            {" · "}
            {p.eventCount} evento{p.eventCount === 1 ? "" : "s"}
            {p.duplicateEvents > 0 ? ` · ${p.duplicateEvents} duplicado(s) ignorado(s)` : ""}
          </p>
          <p className="text-xs text-muted-foreground">
            Primer evento: {fmtDate(p.firstEventAt)} · Último: {fmtDate(p.lastEventAt)}
          </p>
        </div>
        <div className="shrink-0 text-right">
          <p className="text-base font-semibold">
            {formatStripeAmount(p.countedAmount, p.currency ?? "")}
          </p>
          {p.naiveAmount !== p.countedAmount ? (
            <p className="text-[11px] text-muted-foreground line-through">
              {formatStripeAmount(p.naiveAmount, p.currency ?? "")}
            </p>
          ) : null}
          {p.countedRefund ? (
            <p className="text-[11px] text-amber-600">
              reemb. {formatStripeAmount(p.countedRefund, p.currency ?? "")}
            </p>
          ) : null}
        </div>
      </button>

      {open ? (
        <div className="border-t px-4 py-3">
          <p className="mb-2 text-xs text-muted-foreground">
            Monto contado desde:{" "}
            {counted ? (
              <span className="font-medium text-foreground">
                {counted.event_type} · {counted.stripe_event_id}
              </span>
            ) : (
              "ningún evento (no aporta al total)"
            )}
          </p>
          <div className="space-y-2">
            {p.events.map((e) => (
              <div
                key={e.stripe_event_id}
                className={`rounded-md border p-2 text-xs ${
                  e.countedAmount ? "border-emerald-500/50 bg-emerald-500/5" : "opacity-80"
                }`}
              >
                <div className="flex flex-wrap items-center gap-2">
                  {e.countedAmount ? (
                    <span className="inline-flex items-center gap-1 font-medium text-emerald-600">
                      <CheckCircle2 className="h-3.5 w-3.5" /> Contado
                    </span>
                  ) : (
                    <span className="text-muted-foreground">No contado</span>
                  )}
                  <span className="font-medium">{e.event_type ?? "—"}</span>
                  <span className="font-mono text-[11px] text-muted-foreground break-all">
                    {e.stripe_event_id}
                  </span>
                </div>
                <p className="mt-1 text-muted-foreground">
                  {fmtDate(e.received_at)} · {e.outcome ?? "—"}
                  {e.status ? ` / ${e.status}` : ""} ·{" "}
                  {e.currency
                    ? formatStripeAmount(e.amount_received ?? e.amount ?? 0, e.currency)
                    : "sin monto"}
                  {e.processed_at ? " · procesado" : " · pendiente"}
                </p>
                {e.skipReason ? (
                  <p className="mt-0.5 text-[11px] text-muted-foreground">{e.skipReason}</p>
                ) : null}
                {e.countedRefund ? (
                  <p className="mt-0.5 text-[11px] text-amber-600">
                    Reembolso contado desde este evento
                  </p>
                ) : null}
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function StripeReconciliationPage() {
  const [form, setForm] = useState<FormState>(EMPTY);
  const [applied, setApplied] = useState<Omit<StripeWebhookEventsFilters, "cursor">>(
    toFilters(EMPTY),
  );
  const reconcile = useServerFn(reconcileStripePayments);

  const query = useQuery({
    queryKey: ["stripe-reconciliation", applied],
    queryFn: () => reconcile({ data: applied }),
  });

  const set = <K extends keyof FormState>(k: K, v: FormState[K]) =>
    setForm((prev) => ({ ...prev, [k]: v }));

  const duplicates = useMemo(
    () => (query.data?.payments ?? []).filter((p) => p.duplicateEvents > 0).length,
    [query.data],
  );

  return (
    <>
      <SiteHeader />
      <main className="container mx-auto px-4 py-8">
        <AdminNav />
        <header className="mt-6">
          <h1 className="flex items-center gap-2 text-2xl font-semibold">
            <Layers className="h-5 w-5" /> Reconciliación de pagos Stripe
          </h1>
          <p className="mt-1 max-w-3xl text-sm text-muted-foreground">
            Cada tarjeta es un pago único: agrupa todos los eventos que Stripe envió para el mismo
            Payment ID (o Charge / Checkout Session) y marca en verde el evento del que se tomó el
            monto para los totales, evitando que un mismo cobro se sume dos veces.
          </p>
        </header>

        <section className="mt-6 grid gap-3 rounded-lg border bg-card p-4 md:grid-cols-5">
          <div>
            <Label className="text-xs">Desde</Label>
            <Input
              type="datetime-local"
              value={form.from}
              onChange={(e) => set("from", e.target.value)}
            />
          </div>
          <div>
            <Label className="text-xs">Hasta</Label>
            <Input
              type="datetime-local"
              value={form.to}
              onChange={(e) => set("to", e.target.value)}
            />
          </div>
          <div>
            <Label className="text-xs">Entorno</Label>
            <Select
              value={form.env || "all"}
              onValueChange={(v) => set("env", v === "all" ? "" : (v as FormState["env"]))}
            >
              <SelectTrigger>
                <SelectValue placeholder="Todos" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="live">Live</SelectItem>
                <SelectItem value="sandbox">Sandbox</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Moneda</Label>
            <Input
              placeholder="MXN"
              value={form.currency}
              onChange={(e) => set("currency", e.target.value)}
            />
          </div>
          <div>
            <Label className="text-xs">Buscar (pago, cliente, correo)</Label>
            <Input
              placeholder="pi_… / correo"
              value={form.search}
              onChange={(e) => set("search", e.target.value)}
            />
          </div>
          <div className="flex items-end gap-2 md:col-span-5">
            <Button onClick={() => setApplied(toFilters(form))}>
              <Search className="mr-2 h-4 w-4" /> Aplicar
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                setForm(EMPTY);
                setApplied(toFilters(EMPTY));
              }}
            >
              Limpiar
            </Button>
          </div>
        </section>

        <section className="mt-4 grid gap-3 md:grid-cols-3">
          <div className="rounded-lg border bg-card p-4">
            <div className="text-sm font-medium">Pagos únicos</div>
            <p className="mt-1 text-2xl font-semibold">{query.data?.payments.length ?? "—"}</p>
            <p className="text-xs text-muted-foreground">
              {query.data?.eventCount ?? 0} eventos analizados
              {query.data?.truncated ? " (límite 5.000)" : ""}
            </p>
          </div>
          <div className="rounded-lg border bg-card p-4">
            <div className="text-sm font-medium">Duplicados detectados</div>
            <p className="mt-1 text-2xl font-semibold">{duplicates}</p>
            <p className="text-xs text-muted-foreground">
              pagos con más de un evento con monto
            </p>
          </div>
          <div className="rounded-lg border bg-card p-4">
            <div className="text-sm font-medium">Totales</div>
            <div className="mt-1 space-y-0.5 text-xs text-muted-foreground">
              {(query.data?.totals ?? []).length === 0 ? (
                <span>—</span>
              ) : (
                query.data!.totals.map((t) => (
                  <div key={t.currency}>
                    {t.currency}: {formatStripeAmount(t.counted, t.currency)}
                    {t.naive !== t.counted
                      ? ` · sin deduplicar: ${formatStripeAmount(t.naive, t.currency)}`
                      : ""}
                    {t.refunded ? ` · reemb. ${formatStripeAmount(t.refunded, t.currency)}` : ""}
                  </div>
                ))
              )}
            </div>
          </div>
        </section>

        <section className="mt-4 space-y-3">
          {query.isLoading ? (
            <div className="flex items-center gap-2 p-6 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Reconciliando eventos…
            </div>
          ) : query.isError ? (
            <p className="p-6 text-sm text-destructive">
              No se pudo cargar la reconciliación. Verifica tus permisos e intenta de nuevo.
            </p>
          ) : (query.data?.payments.length ?? 0) === 0 ? (
            <p className="p-6 text-sm text-muted-foreground">
              No hay eventos de Stripe para estos filtros.
            </p>
          ) : (
            query.data!.payments.map((p) => <PaymentGroup key={p.paymentKey} p={p} />)
          )}
        </section>
      </main>
    </>
  );
}
